import React, { Fragment } from 'react';
import { number } from 'prop-types';
import dynamic from 'next/dynamic';
import { AppWithGlobalState } from 'components/App';
import DetectAmp from 'components/DetectAmp';
import Metadata from 'components/Metadata';
import { HAS_RECIRC, SITE_ERROR_MESSAGE } from 'constants/site';
import { ERROR_NOT_FOUND, ERROR_GENERIC } from 'constants/pageTypes';
import { createMetadataObject } from 'util/metadata';
import {
  generatePageBundlesFromMetadata,
  generateMetadataBundleItems
} from 'util/metadataBundle';
import styles from './styles';

const Recirc = dynamic(() => import('components/Recirc'), {
  ssr: false,
  loading: () => null
});

function getHttpStatus(statusCode) {
  let status;
  switch (statusCode) {
    case 404:
      status = `${statusCode}: Not Found`;
      break;
    case 410:
      status = `${statusCode}: Gone`;
      break;
    case 500:
      status = `${statusCode}: Internal Server Error`;
      break;
    case 502:
      status = `${statusCode}: Bad Gateway`;
      break;
    case 503:
      status = `${statusCode}: Service Unavailable`;
      break;
    case 504:
      status = `${statusCode}: Gateway Timeout`;
      break;
    default:
      status = `${statusCode}`;
  }
  return status;
}

function ErrorPage({ statusCode }) {
  let title = 'An Unexpected Error Has Occurred';

  if (statusCode === 404) {
    title = "Whoops! Apparently that page doesn't exist.";
  } else if (statusCode === 410) {
    title = 'Whoops! Apparently that page may have been moved or deleted.';
  }

  // CLEANUP: rethink how to categorize/track error pages
  const pageType = statusCode === 404 ? ERROR_NOT_FOUND : ERROR_GENERIC;
  const httpStatus = getHttpStatus(statusCode);

  if (statusCode === undefined) {
    // Something really bad has happened...
    return <pre style={{ padding: '1rem' }}>{title}</pre>;
  }

  const metadata = createMetadataObject({
    title: `${httpStatus}`
  });

  return (
    <DetectAmp>
      {isAmp =>
        isAmp ? (
          <Fragment>
            <Metadata items={generateMetadataBundleItems(metadata)} />
            <pre>{httpStatus}</pre>
            <style jsx>{`
              pre {
                padding: 1rem;
                text-align: center;
              }
            `}</style>
          </Fragment>
        ) : (
          <AppWithGlobalState
            pageType={pageType}
            pageBundles={generatePageBundlesFromMetadata(metadata)}
            isLoading={false}
            httpStatus={httpStatus}
          >
            <div className="ErrorPage">
              <div className="ErrorPage__above">
                <div className="ErrorPage__above-message">
                  <h1>{SITE_ERROR_MESSAGE}</h1>
                  {statusCode ? (
                    <p>
                      {title} (Error Code {statusCode})
                    </p>
                  ) : null}
                </div>
              </div>
              {HAS_RECIRC && (
                <div className="ErrorPage__below ">
                  <Recirc
                    name="ErrorPage__below"
                    design="image"
                    title="You might also like"
                  />
                </div>
              )}
            </div>
            <style jsx>{styles}</style>
          </AppWithGlobalState>
        )
      }
    </DetectAmp>
  );
}

ErrorPage.propTypes = {
  statusCode: number.isRequired
};

export default ErrorPage;
